import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { client } from "@/api"

// Mirrors the monorepo's PROCESSING_STAGES / STAGE_STATUSES (backend/db/tables/clips.ts).
// Kept local to the admin app (same pattern as useDetectJobs' DetectJob type).
//
// `fragment` (HLS-fragmentation) is observability-ONLY: it appears as its own stage in the
// Recent Operations event timeline but has NO denormalized status column on a clip and is
// NOT a retryable stage (the backend's retry endpoint rejects it). So it belongs in
// PROCESSING_STAGES (the full set of stages an event can report, used by the Recent Ops
// stage filter) but NOT in RETRYABLE_STAGES (the per-clip columned stages the Clips page
// displays + can retry). Keep the two sets distinct so no fragment retry control is ever
// rendered (it would 400).
export const PROCESSING_STAGES = ["transcode", "cover", "ai", "fragment"] as const
export type ProcessingStage = (typeof PROCESSING_STAGES)[number]

// The stages that have a per-clip status column AND a per-stage worker-retry path. Excludes
// `fragment` (observability-only). Mirrors the backend's RETRYABLE_STAGES
// (backend/src/routes/admin/clips/index.ts). The Clips page renders one column per stage
// here and offers a retry button per stage here.
export const RETRYABLE_STAGES = ["transcode", "cover", "ai"] as const
export type RetryableStage = (typeof RETRYABLE_STAGES)[number]

export const STAGE_STATUSES = ["pending", "running", "succeeded", "failed", "skipped"] as const
export type StageStatus = (typeof STAGE_STATUSES)[number]

// One row from GET /admin/clips (#692): the per-stage status columns are display-derived
// for legacy rows server-side, and the game public_id is joined in.
export interface Clip {
  public_id: string
  game_public_id: string | null
  video_url: string
  cover_image: string | null
  start_seconds: number | null
  end_seconds: number | null
  // Worker-reported metadata (JSON). The display gate reads a whole-file clip's
  // duration from here (clipStageStatus videoMetaDurationSeconds).
  video_meta: unknown
  size: number | null
  created_at: string
  // Real row-creation time for virtual (split) clips: /split copies the parent's
  // created_at onto children, so the effective "created" time is sliced_at ?? created_at.
  // Null for whole-file uploads and pre-migration rows.
  sliced_at: string | null
  transcode_status: StageStatus
  cover_status: StageStatus
  ai_status: StageStatus
  transcode_updated_at: string | null
  cover_updated_at: string | null
  ai_updated_at: string | null
}

// One row from GET /admin/clips/:publicId/processing-events (#692): the append-only
// attempt timeline. Internal ids are omitted server-side (Invariant #1).
export interface ClipProcessingEvent {
  stage: ProcessingStage
  status: StageStatus
  attempt: number
  error: string | null
  duration_ms: number | null
  detail: Record<string, unknown> | null
  created_at: string
}

export type StatusFilter = "all" | StageStatus
export type StageFilter = "all" | ProcessingStage

interface UseClipsOptions {
  status?: StatusFilter
  stage?: StageFilter
  game?: string
  limit?: number
}

export function useClips({
  status = "all",
  stage = "all",
  game = "",
  limit = 200,
}: UseClipsOptions = {}) {
  return useQuery<Clip[]>({
    queryKey: ["admin", "clips", { status, stage, game, limit }],
    queryFn: async () => {
      const query = {
        limit: limit.toString(),
        ...(status !== "all" ? { status } : {}),
        ...(stage !== "all" ? { stage } : {}),
        ...(game.trim() ? { game: game.trim() } : {}),
      }
      const response = await client.admin.clips.$get({ query })
      if (!response.ok) {
        throw new Error(`Failed to fetch clips: ${response.status}`)
      }
      return response.json() as Promise<Clip[]>
    },
    // No refetchIntervalInBackground (2026-09-28 incident): a tab left open kept
    // polling every 5s around the clock. installIdlePause (@playside/tab-activity, wired
    // onto this app's QueryClient) already pauses this while the tab is hidden or idle and
    // resumes it on the next activity.
    refetchInterval: 5000,
  })
}

export function useClipProcessingEvents(publicId: string | null) {
  return useQuery<ClipProcessingEvent[]>({
    queryKey: ["admin", "clips", publicId, "processing-events"],
    queryFn: async () => {
      const response = await client.admin.clips[":publicId"]["processing-events"].$get({
        param: { publicId: publicId as string },
      })
      if (!response.ok) {
        throw new Error(`Failed to fetch processing events: ${response.status}`)
      }
      return response.json() as Promise<ClipProcessingEvent[]>
    },
    enabled: !!publicId,
    refetchInterval: 5000,
  })
}

export function useRetryClipStage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ publicId, stage }: { publicId: string; stage: RetryableStage }) => {
      const response = await client.admin.clips[":publicId"].retry.$post({
        param: { publicId },
        json: { stage },
      })
      if (!response.ok) {
        throw new Error(`Retry failed: ${response.status}`)
      }
      return response.json()
    },
    onSuccess: (_data, { publicId }) => {
      // Refresh the list (the stage just flipped to pending) and the open timeline.
      queryClient.invalidateQueries({ queryKey: ["admin", "clips"] })
      queryClient.invalidateQueries({
        queryKey: ["admin", "clips", publicId, "processing-events"],
      })
    },
  })
}
